import { TOAST_IDS } from '@/config/toast-ids';
import { reportError } from '@/lib/telemetry';
import { showToast } from '@/lib/ui/toast';

import {
  buildMorfiVortexLogoSvg,
  MORFI_VORTEX_VIEWBOX_HEIGHT,
  MORFI_VORTEX_VIEWBOX_WIDTH,
} from './morfi-vortex-wordmark';

const PNG_EXPORT_SCALE = 4;
const PNG_ASSET_LONG_EDGE = 1600;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load logo image'));
    image.src = url;
  });
}

export function resolveMorfiVortexLogoFill(logoRoot: HTMLElement | null): string {
  if (logoRoot === null) {
    return '#ffffff';
  }

  const path = logoRoot.querySelector('path');
  if (path !== null) {
    const pathFill = getComputedStyle(path).fill;
    if (pathFill !== '' && pathFill !== 'none') {
      return pathFill;
    }
  }

  const color = getComputedStyle(logoRoot).color;
  return color !== '' ? color : '#ffffff';
}

export async function copyMorfiVortexLogoSvg(
  fill: string,
  successMessage: string,
  failureMessage: string,
): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(buildMorfiVortexLogoSvg(fill));
    showToast({
      id: TOAST_IDS.MORFI_LOGO_SVG_COPIED,
      message: successMessage,
      variant: 'success',
    });
    return true;
  } catch (error) {
    reportError(error, { component: 'copyMorfiVortexLogoSvg' });
    showToast({
      id: TOAST_IDS.MORFI_LOGO_COPY_FAILED,
      message: failureMessage,
      variant: 'error',
    });
    return false;
  }
}

export async function copyMorfiLogoSvgAsset(
  assetUrl: string,
  successMessage: string,
  failureMessage: string,
): Promise<boolean> {
  try {
    const response = await fetch(assetUrl);
    if (!response.ok) {
      throw new Error(`Logo asset request failed with status ${response.status}`);
    }
    await navigator.clipboard.writeText(await response.text());
    showToast({
      id: TOAST_IDS.MORFI_LOGO_SVG_COPIED,
      message: successMessage,
      variant: 'success',
    });
    return true;
  } catch (error) {
    reportError(error, { component: 'copyMorfiLogoSvgAsset' });
    showToast({
      id: TOAST_IDS.MORFI_LOGO_COPY_FAILED,
      message: failureMessage,
      variant: 'error',
    });
    return false;
  }
}

export async function copyMorfiLogoPngAsset(
  assetUrl: string,
  successMessage: string,
  failureMessage: string,
): Promise<boolean> {
  try {
    const image = await loadImage(assetUrl);
    const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);
    if (longestEdge === 0) {
      throw new Error('Logo asset has no intrinsic dimensions');
    }
    const scale = PNG_ASSET_LONG_EDGE / longestEdge;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('Canvas unavailable');
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const pngBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png');
    });
    if (pngBlob === null) {
      throw new Error('PNG export failed');
    }
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
    showToast({
      id: TOAST_IDS.MORFI_LOGO_PNG_COPIED,
      message: successMessage,
      variant: 'success',
    });
    return true;
  } catch (error) {
    reportError(error, { component: 'copyMorfiLogoPngAsset' });
    showToast({
      id: TOAST_IDS.MORFI_LOGO_COPY_FAILED,
      message: failureMessage,
      variant: 'error',
    });
    return false;
  }
}

export async function copyMorfiVortexLogoPng(
  fill: string,
  successMessage: string,
  failureMessage: string,
): Promise<boolean> {
  const svg = buildMorfiVortexLogoSvg(fill);
  const width = MORFI_VORTEX_VIEWBOX_WIDTH * PNG_EXPORT_SCALE;
  const height = MORFI_VORTEX_VIEWBOX_HEIGHT * PNG_EXPORT_SCALE;
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  try {
    const image = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('Canvas unavailable');
    }

    context.drawImage(image, 0, 0, width, height);
    const pngBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png');
    });
    if (pngBlob === null) {
      throw new Error('PNG export failed');
    }

    await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
    showToast({
      id: TOAST_IDS.MORFI_LOGO_PNG_COPIED,
      message: successMessage,
      variant: 'success',
    });
    return true;
  } catch (error) {
    reportError(error, { component: 'copyMorfiVortexLogoPng' });
    showToast({
      id: TOAST_IDS.MORFI_LOGO_COPY_FAILED,
      message: failureMessage,
      variant: 'error',
    });
    return false;
  } finally {
    URL.revokeObjectURL(url);
  }
}
